import { getProfileForCategory } from '@prism-ehr/shared/constants';
import { resolveCaseBillingCoverage } from '@prism-ehr/shared/utils/caseBillingCoverage';

const normalizeName = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const nameTokens = (s) => (s || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
const namesAgree = (a, b) => {
  const ta = nameTokens(a);
  const tb = new Set(nameTokens(b));
  return ta.some(t => tb.has(t));
};

// Stored-id-first matching: insurance entries stamped at write time carry
// organization_payer_id, the exact row the user picked — no guessing needed.
// Legacy/unstamped entries fall through to the fuzzy chain: claimmd-ID-first
// with a name-disagreement guard (an eligibility-stamped claimmd_payer_id can
// point at the wrong org payer — observed: a commercial chart's id matching
// the Medicare record). When the ID-matched payer's name shares no tokens
// with the insurance's payer name AND an exact normalized name match to a
// different payer exists, the name match wins. Resolution is silent by
// design — clinicians never see payer-conflict warnings.
export const resolveOrgPayer = (payers, organizationPayerId, claimmdPayerId, payerName) => {
  if (organizationPayerId) {
    const stored = payers.find(p => String(p.id) === String(organizationPayerId));
    // A stale stored id (payer deleted/deactivated) falls through to the chain
    if (stored) return stored;
  }
  const idMatch = claimmdPayerId
    ? payers.find(p => p.claimmd_payer_id && String(p.claimmd_payer_id) === String(claimmdPayerId)) || null
    : null;
  const normName = normalizeName(payerName);
  const nameMatch = normName
    ? payers.find(p => normalizeName(p.display_name) === normName) || null
    : null;

  if (!idMatch) return nameMatch;
  if (!payerName || namesAgree(idMatch.display_name, payerName)) return idMatch;
  if (nameMatch && nameMatch.id !== idMatch.id) return nameMatch;
  return idMatch;
};

// Medicare / MA plans always run the stock Medicare rule flags, but the payer's
// own contract limits (unit cap, prohibited codes) and the manual-units entry
// preference still apply on top of them.
const medicareConfig = (orgPayer) => {
  const base = getProfileForCategory('medicare')?.config || null;
  const own = orgPayer?.billing_config;
  if (!base || !own) return base;
  return {
    ...base,
    ...(own.max_units_per_visit != null ? { max_units_per_visit: own.max_units_per_visit } : {}),
    ...(own.prohibited_cpt_codes?.length ? { prohibited_cpt_codes: own.prohibited_cpt_codes } : {}),
    ...(own.manual_units === true ? { manual_units: true } : {}),
  };
};

export function resolvePayerBillingConfig(patient, caseRow, payers = [], isLoading = false) {
  const empty = { billingConfig: null, alertAppointments: null, alertPatientChart: null, payerCategory: null, orgPayer: null, coverage: null, isLoading };
  if (isLoading) return empty;
  const insurance = Array.isArray(patient?.insurance) ? patient.insurance : patient?.insurance ? [patient.insurance] : [];
  const coverage = resolveCaseBillingCoverage(caseRow, { insurance });
  const selected = coverage.insurance;
  if (!selected || coverage.mode === 'self_pay') return { ...empty, coverage };
  const orgPayer = resolveOrgPayer(payers, selected.organization_payer_id, selected.claimmd_payer_id, selected.payer_name);
  const planType = (selected.plan_type || '').toLowerCase();
  const medicare = planType === 'medicare' || planType === 'medicare_advantage';
  const category = medicare ? 'medicare' : orgPayer?.payer_category || planType;
  return {
    billingConfig: medicare ? medicareConfig(orgPayer) : orgPayer?.billing_config || getProfileForCategory(category)?.config || null,
    alertAppointments: orgPayer?.alert_appointments || null,
    alertPatientChart: orgPayer?.alert_patient_chart || null,
    payerCategory: category || null,
    orgPayer: orgPayer || null,
    coverage,
    isLoading: false,
  };
}
